import React from 'react';
import { Badge } from 'features/catalog/useBadges';
import { computeRarityCounts, getRarityImageUrlByName, RARITY_TIERS, RarityCounts } from 'features/catalog/rarity';

interface RarityCountsDisplayProps {
  /** Rarity counts to display. Tiers with count 0 are hidden. */
  counts: RarityCounts;
  /** CSS class for each rarity item wrapper. */
  itemClassName?: string;
  /** CSS class for each rarity icon image. */
  iconClassName?: string;
  /** Optional inline styles for the icon images. */
  iconStyle?: React.CSSProperties;
}

/**
 * Renders a row of rarity icons with counts (e.g. [mythic icon] 3 [legendary icon] 5).
 * Only non-zero tiers are displayed.
 */
export const RarityCountsDisplay: React.FC<RarityCountsDisplayProps> = ({
  counts,
  itemClassName = 'rarity-count-item',
  iconClassName = 'rarity-count-icon',
  iconStyle,
}) => (
  <>
    {RARITY_TIERS.map(
      ({ key, label }) =>
        counts[key] > 0 && (
          <span key={key} className={itemClassName}>
            <img src={getRarityImageUrlByName(key)} alt={label} className={iconClassName} style={iconStyle} />
            <span>{counts[key]}</span>
          </span>
        )
    )}
  </>
);

interface GalleryRarityCountsProps {
  counts: RarityCounts;
}

/** Shared gallery-header rarity row. Hidden when every tier is 0. */
export const GalleryRarityCounts: React.FC<GalleryRarityCountsProps> = ({ counts }) => {
  const summary = RARITY_TIERS.filter(({ key }) => counts[key] > 0)
    .map(({ key, label }) => `${counts[key]} ${label}`)
    .join(', ');
  if (!summary) return null;

  return (
    <span className="gallery-group-rarities" aria-label={summary}>
      <RarityCountsDisplay
        counts={counts}
        itemClassName="gallery-group-rarity"
        iconClassName="gallery-group-rarity-icon"
      />
    </span>
  );
};

interface GalleryGroupRarityCountsProps {
  /** Badge IDs that belong to this group only. */
  badgeIds: readonly string[];
  badgesMap: Map<string, Badge>;
}

/** Rarity summary for the given badge IDs (catalog, claimed, or still-unclaimed — caller decides). */
export const GalleryGroupRarityCounts: React.FC<GalleryGroupRarityCountsProps> = ({ badgeIds, badgesMap }) => (
  <GalleryRarityCounts counts={computeRarityCounts(badgeIds, badgesMap)} />
);
